import { Router } from '@angular/router';
import { Component, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filter, switchMap } from 'rxjs/operators';
import { FormGroup, Validators, FormBuilder, FormControl } from '@angular/forms';

import { BrowserService } from '../../../../services/browser/browser.service';
import { NotificationService } from '../../../../services/notification.service';
import { ImporterService } from '../../../../services/importer/importer.service';
import { MimeTypeValidator } from '../../../../shared/validators/files/mime-type-validator/mime-type-validator';
import { FileService } from '../../../../services/file/file.service';
import { Relate } from '../../../../objects/relate';
import { RecordService } from '../../../../services/record.service';
import { LooseObject } from '../../../../objects/loose-object';
import { Users } from '../../../../objects/users';

import * as _moment from 'moment';
import { daysMultiselectValidator } from '../../../../shared/validators/days-multiselect-validator';
import { get } from 'lodash-es';
import { filled } from '../../../../shared/utils/common';

const moment = (_moment as any).default ? (_moment as any).default : _moment;
const DAYS_OF_WEEK_OPTIONS: { id: string, text:string }[] = [
  { id: "sunday", text: "sunday" },
  { id: "monday", text: "monday" },
  { id: "tuesday", text: "tuesday" },
  { id: "wednesday", text: "wednesday" },
  { id: "thursday", text: "thursday" },
  { id: "friday", text: "friday" },
  { id: "saturday", text: "saturday" },
];

@Component({
  selector: 'booking-page-setup-form',
  templateUrl: './booking-page-setup-form.component.html',
  styleUrls: ['./booking-page-setup-form.component.scss']
})
export class BookingPageSetupFormComponent implements OnInit {
  referenceStartDate: Date;
  referenceEndDate: Date;

  objFieldsMetadata = {
    user_id: {
      required: true,
      readonly: false,
      is_admin: false,
      default_value: null,
      key: 'user_id',
      label: 'select_queuing_user',
      controlType: 'relate',
      module: 'users',
      multiple: false,
      options: [],
      add_tag: false,
      filter: {
        level: 'queuing'
      }
    },
    duration: {
      controlType: 'number',
      key: 'duration',
      label: 'booking_duration_in_minutes',
      required: true,
      default_value: 60,
      precision: 0,
      min_length: 1,
      default_text: 'Duration in minutes',
      max_length: 60 * 24, // 60 minutes * 8 Hours limit
    },
    days_selected: {
      controlType: 'multiselect',
      key: 'days_selected',
      label: 'days_selected',
      multiple: true,
      required: true,
      default_value: null,
      default_text: '',
      options: DAYS_OF_WEEK_OPTIONS
    },
    start_time: {
      controlType: 'timer',
      key: 'start_time',
      label: 'booking_start_time',
      required: true,
      default_value: {date: 'today', hour: 8, minute: 0, meridiem: 'am'},
      default_text: '',
    },
    end_time: {
      controlType: 'timer',
      key: 'end_time',
      label: 'booking_end_time',
      required: true,
      default_value: {date: 'today', hour: 5, minute: 0, meridiem: 'pm'},
      default_text: '',
    },
    start_date: {
      controlType: 'date',
      key: 'start_date',
      label: 'booking_start_date',
      required: true,
      default_value: null,
      default_text: '',
    },
    end_date: {
      controlType: 'date',
      key: 'end_date',
      label: 'booking_end_date',
      required: true,
      default_value: null,
      default_text: '',
    },
  };

  /**
   *
   * @type {FormGroup}
   */
  form: FormGroup;

  strModule: string = 'booking_pages';
  bSubmitted: boolean = false;
  bShowLoader: boolean = false;
  bValidForm: boolean = false;

  /**
   * Here, we'll store the ID of the opened record from the module source
   * @type { string | null }
   */
  protected recordId: string | null = null;

  /**
   * List of all queuing users
   * @type { string | null }
   */
  protected queuing_users: object[] | null = null;

  public users = new Relate<Users>();

  constructor(
    protected router: Router,
    protected formBuilder: FormBuilder,
    protected browserService: BrowserService,
    protected importerService: ImporterService,
    protected translateService: TranslateService,
    protected fileService: FileService,
    protected notificationService: NotificationService,
    protected mimeTypeValidator: MimeTypeValidator,
    protected recordService: RecordService,
    protected dialogRef: MatDialogRef<BookingPageSetupFormComponent>,
    @Inject(MAT_DIALOG_DATA) private objData: BookingPageSetupFormComponentDialogData,
  ) { }

  ngOnInit() {
    this.recordId = get(this.objData, 'parent_record.id');
    this._initMetadata();
  }

  /**
   * Initialize Site Relate Data
   */
  initQueuingUserList() {
    let objFilter = {
      level: 'queuing'
    };
    this.recordService
      .getRecordRelate('users', '', [], null, objFilter)
      .subscribe(usersResponse => {
        this.users = new Relate<Users>().buildRelates(
          // upon input search
          switchMap(keyword => this.recordService.getRecordRelate(
            'users', keyword, [], null, objFilter
          )),
          usersResponse
        );
      })
  }

  /**
   * initialize form
   */
  initFormGroup() {
    this.form = this.formBuilder.group({
      // empty default
      user_id: new FormControl(
        get(this.objFieldsMetadata, 'user_id.default_value', null),
        [Validators.required]
      ),
      // default values need to be set
      days_selected: new FormControl(
        get(this.objFieldsMetadata, 'days_selected.default_value', ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']),
        [Validators.required, daysMultiselectValidator()]
      ),
      duration: new FormControl(
        get(this.objFieldsMetadata, 'duration.default_value', null),
        [Validators.required, Validators.max(60 * 24), Validators.min(1)]
      ),
      // form value are set upon initialization via objField value
      start_time: new FormControl(
        get(this.objFieldsMetadata, 'start_time.default_value', null),
        [Validators.required]
      ),
      end_time: new FormControl(
        get(this.objFieldsMetadata, 'end_time.default_value', null),
        [Validators.required]
      ),
      start_date: new FormControl(
        get(this.objFieldsMetadata, 'start_date.default_value', null),
        [Validators.required]
      ),
      end_date: new FormControl(
        get(this.objFieldsMetadata, 'end_date.default_value', null),
        [Validators.required]
      ),
    });
  }

  /**
   * save record
   */
  onSubmit() {
    this.bSubmitted = true;
    this.bShowLoader = true;

    if (this.form.valid) {
      this.recordService.saveRecord('booking_pages', this.form.getRawValue(), this.recordId)
      .subscribe( () => {
        this.closeDialog(true);
      }, () => {
        this.notificationService.notifyError('please_contact_admin_for_assistance');
        this.bSubmitted = true;
        this.bShowLoader = true;
      })
    }
  }

  /**
   * close dialog
   *
   * @returns {void}
   */
  closeDialog(response: boolean = false): void {
    this.dialogRef.close(response);
  }


  /**
   * For Cancel button
   */
  cancelDialog(): void {
    if (this.checkFormGroupDirty()) {
      // Pop-up modal for confirmation
      this.notificationService.sendConfirmation('confirm_cancel')
        .pipe(
          filter(confirmation => confirmation.answer === true),
        )
        .subscribe(() => {
          this.closeDialog();
        });
    } else {
      this.closeDialog();
    }
  }

  /**
   * Check if the form group is changed
   *
   * @returns {boolean}
   */
  checkFormGroupDirty(): boolean {
    return this.form.dirty;
  }

  private _initMetadata(): void {
    let objReferenceStartDate = new Date();
    let objReferenceEndDate = new Date(objReferenceStartDate);

    let strStartDate = objReferenceStartDate.toISOString().slice(0, 10);
    if (get(this.objData, 'parent_record.starting_at')) {
      strStartDate = get(this.objData, 'parent_record.starting_at');
    }

    let strEndDate = objReferenceEndDate.toISOString().slice(0, 10);
    if (filled(get(this.objData, 'parent_record.days_range', 0))) {
      strEndDate = moment(strStartDate).add(this.objData.parent_record.days_range, 'day').toISOString().slice(0, 10)
    }


    let objStartTime = {
      date: 'today',
      hour: 8,
      minute: 0,
      meridiem: 'am'
    };
    if (filled(this.objData.parent_record.time_start)) {
      let objTime = moment(this.objData.parent_record.time_start, 'HH:mm::ss');
      objStartTime.hour = objTime.hour();
      objStartTime.minute = objTime.minute();
    }
    let objEndTime = {
      date: 'today',
      hour: 8,
      minute: 0,
      meridiem: 'am'
    };
    if (filled(this.objData.parent_record.time_end)) {
      let objTime = moment(this.objData.parent_record.time_end, 'HH:mm::ss');
      objEndTime.hour = objTime.hour();
      objEndTime.minute = objTime.minute();
    }

    // set metadata default values
    this.objFieldsMetadata.user_id.default_value = get(this.objData, 'parent_record.user_id', null);
    if (filled(this.objFieldsMetadata.user_id.default_value)) {
      this.objFieldsMetadata.user_id['default_text'] = get(this.objData, 'parent_record.user_text', null);
    }
    this.objFieldsMetadata.days_selected.default_value = get(
      this.objData,
      'parent_record.days_selected',
      [
        'monday', 'tuesday', 'wednesday', 'thursday', 'friday'
      ]
    );
    this.objFieldsMetadata.duration.default_value = get(this.objData, 'parent_record.duration', 60);
    this.objFieldsMetadata.start_time.default_value = objStartTime;
    this.objFieldsMetadata.end_time.default_value = objEndTime;
    this.objFieldsMetadata.start_date.default_value = strStartDate;
    this.objFieldsMetadata.end_date.default_value = strEndDate;

    // set form default values
    this.form = this.formBuilder.group({
      user_id: new FormControl(
        this.objFieldsMetadata.user_id.default_value,
        [Validators.required]
      ),
      days_selected: new FormControl(
        this.objFieldsMetadata.days_selected.default_value,
        [Validators.required, daysMultiselectValidator()]
      ),
      duration: new FormControl(
        this.objFieldsMetadata.duration.default_value,
        [Validators.required, Validators.max(60 * 24), Validators.min(1)]
      ),
      start_time: new FormControl(
        this.objFieldsMetadata.start_time.default_value,
        [Validators.required]
      ),
      end_time: new FormControl(
        this.objFieldsMetadata.end_time.default_value,
        [Validators.required]
      ),
      start_date: new FormControl(
        this.objFieldsMetadata.start_date.default_value,
        [Validators.required]
      ),
      end_date: new FormControl(
        this.objFieldsMetadata.end_date.default_value,
        [Validators.required]
      ),
    });
  }
}

export type BookingPageSetupFormComponentDialogData = {
  module: string;
  parent_record?: LooseObject;
  additional_mappings?: Record<string, any>;
}